import { HttpClient } from '@wix/http-client'

export class ErrorWithAdditionalData<
  T extends Record<string, unknown> = Record<string, unknown>,
> extends Error {
  data?: T
  constructor({
    message,
    data,
    cause,
  }: {
    message: string
    data?: T
    cause?: unknown
  }) {
    super(message, { cause })
    this.data = data
    this.name = 'ErrorWithAdditionalData'
  }
}

export const createError = (
  message: string,
  {
    data,
    cause,
  }: {
    data?: Record<string, unknown>
    cause?: unknown
  } = {},
) => new ErrorWithAdditionalData({ message, cause, data })

export type ErrorLike = { message: string }

const isErrorLike = (value: unknown): value is ErrorLike => {
  return (
    typeof value === 'object' &&
    value !== null &&
    'message' in value &&
    typeof value.message === 'string'
  )
}

export const findError = <T extends ErrorLike = ErrorLike>(
  originalError: unknown,
  test: (error: ErrorLike) => error is T,
): T | undefined => {
  let error = originalError
  while (isErrorLike(error)) {
    if (test(error)) {
      return error
    }
    if (!('cause' in error)) {
      break
    }
    error = error.cause
  }
  return
}

export const isMissingPermissionsError = (
  error: ErrorLike,
): error is ErrorLike =>
  HttpClient.isHttpError(error) && error.response?.status === 403

export const isCollectionNotFoundError = (
  error: ErrorLike,
): error is ErrorLike => error.message.startsWith('WDE0025:')

export const isWixDataConflictError = (error: ErrorLike): error is ErrorLike =>
  error.message.startsWith('WDE0300:')

const WIX_CODE_DISABLED_MODE_ERROR =
  'Wix code cannot work in disabled save mode'

export const isWixCodeDisabledModeError = (
  error: ErrorLike,
): error is ErrorLike => error.message === WIX_CODE_DISABLED_MODE_ERROR

export const createCollectionNotFoundError = (collectionId: string) =>
  createError(
    'WDE0025: The collection does not exist. You cannot work with a collection using the Data API before it is created in the Editor.',
    { data: { collectionId } },
  )

export const isError = (error: unknown): error is Error =>
  error instanceof Error

export const findErrorByClass = <T extends Error>(
  error: unknown,
  ErrorClass: new (...args: any[]) => T,
): T | null => {
  if (!error) {
    return null
  }
  if (error instanceof ErrorClass) {
    return error
  }
  if (isErrorLike(error) && 'cause' in error) {
    return findErrorByClass(error.cause, ErrorClass)
  }
  return null
}
